/**
 * GENERATED FILE — do not hand-edit. Run `node scripts/gen-cahs-icons.mjs`.
 *
 * CAHS marker glyph manifest (#303/#304/#411). Raw monochrome single-path SVG
 * strings (`fill`/`stroke="currentColor"`) keyed by glyph name, plus role/
 * category metadata. Consumed by iconRegistry's CAHS retint path — the
 * `currentColor` ink is substituted with a family token at render time.
 *
 * MIXED PROVENANCE (see scripts/gen-cahs-icons.mjs): all 34 hazard + asset glyphs
 * are the REAL Public Safety Canada CAHS symbols (the #307 real-asset path,
 * counsel-cleared — attribution in NOTICE.txt). Only the 3 category frames are
 * original TSR clean-room reconstructions.
 */
import fire from './fire.svg?raw';
import flood from './flood.svg?raw';
import tornado from './tornado.svg?raw';
import hurricane from './hurricane.svg?raw';
import iceStorm from './ice-storm.svg?raw';
import blizzard from './blizzard.svg?raw';
import extremeHeat from './extreme-heat.svg?raw';
import severeStorm from './severe-storm.svg?raw';
import hazmat from './hazmat.svg?raw';
import smoke from './smoke.svg?raw';
import hotSpot from './hot-spot.svg?raw';
import airQuality from './air-quality.svg?raw';
import avalanche from './avalanche.svg?raw';
import biohazard from './biohazard.svg?raw';
import chemical from './chemical.svg?raw';
import landslide from './landslide.svg?raw';
import nuclearFacility from './nuclear-facility.svg?raw';
import radiological from './radiological.svg?raw';
import railIncident from './rail-incident.svg?raw';
import commsTower from './comms-tower.svg?raw';
import hospital from './hospital.svg?raw';
import emergencyOpsCentre from './emergency-ops-centre.svg?raw';
import fireStation from './fire-station.svg?raw';
import stagingArea from './staging-area.svg?raw';
import assemblyArea from './assembly-area.svg?raw';
import ambulance from './ambulance.svg?raw';
import medevac from './medevac.svg?raw';
import decontamination from './decontamination.svg?raw';
import evacuation from './evacuation.svg?raw';
import emergencyShelter from './emergency-shelter.svg?raw';
import police from './police.svg?raw';
import waterDistribution from './water-distribution.svg?raw';
import power from './power.svg?raw';
import roadClosure from './road-closure.svg?raw';
import frameIncident from './frame-incident.svg?raw';
import frameInfrastructure from './frame-infrastructure.svg?raw';
import frameOperations from './frame-operations.svg?raw';

/** Every CAHS glyph key (hazard glyphs · asset glyphs · category frames). */
export type CahsGlyphKey =
  | 'fire'
  | 'flood'
  | 'tornado'
  | 'hurricane'
  | 'ice-storm'
  | 'blizzard'
  | 'extreme-heat'
  | 'severe-storm'
  | 'hazmat'
  | 'smoke'
  | 'hot-spot'
  | 'air-quality'
  | 'avalanche'
  | 'biohazard'
  | 'chemical'
  | 'landslide'
  | 'nuclear-facility'
  | 'radiological'
  | 'rail-incident'
  | 'comms-tower'
  | 'hospital'
  | 'emergency-ops-centre'
  | 'fire-station'
  | 'staging-area'
  | 'assembly-area'
  | 'ambulance'
  | 'medevac'
  | 'decontamination'
  | 'evacuation'
  | 'emergency-shelter'
  | 'police'
  | 'water-distribution'
  | 'power'
  | 'road-closure'
  | 'frame-incident'
  | 'frame-infrastructure'
  | 'frame-operations';

/** What register a glyph belongs to: act-register hazard, ambient asset, or a frame. */
export type CahsGlyphRole = 'hazard' | 'asset' | 'frame';

/** CAHS category grammar (frame shape): incident ◇ · infrastructure ▭ · operations ○. */
export type CahsCategory = 'incident' | 'infrastructure' | 'operations';

export interface CahsGlyphMeta {
  role: CahsGlyphRole;
  category: CahsCategory;
  label: string;
}

/** Raw SVG markup per glyph key (monochrome, single `<path>`, inked currentColor). */
export const CAHS_GLYPHS: Record<CahsGlyphKey, string> = {
  'fire': fire,
  'flood': flood,
  'tornado': tornado,
  'hurricane': hurricane,
  'ice-storm': iceStorm,
  'blizzard': blizzard,
  'extreme-heat': extremeHeat,
  'severe-storm': severeStorm,
  'hazmat': hazmat,
  'smoke': smoke,
  'hot-spot': hotSpot,
  'air-quality': airQuality,
  'avalanche': avalanche,
  'biohazard': biohazard,
  'chemical': chemical,
  'landslide': landslide,
  'nuclear-facility': nuclearFacility,
  'radiological': radiological,
  'rail-incident': railIncident,
  'comms-tower': commsTower,
  'hospital': hospital,
  'emergency-ops-centre': emergencyOpsCentre,
  'fire-station': fireStation,
  'staging-area': stagingArea,
  'assembly-area': assemblyArea,
  'ambulance': ambulance,
  'medevac': medevac,
  'decontamination': decontamination,
  'evacuation': evacuation,
  'emergency-shelter': emergencyShelter,
  'police': police,
  'water-distribution': waterDistribution,
  'power': power,
  'road-closure': roadClosure,
  'frame-incident': frameIncident,
  'frame-infrastructure': frameInfrastructure,
  'frame-operations': frameOperations,
};

/** Role / category / label metadata per glyph (for #305 family keying + legend). */
export const CAHS_GLYPH_META: Record<CahsGlyphKey, CahsGlyphMeta> = {
  'fire': { role: 'hazard', category: 'incident', label: "Forest fire / wildfire" },
  'flood': { role: 'hazard', category: 'incident', label: "Flood / flash flood" },
  'tornado': { role: 'hazard', category: 'incident', label: "Tornado / waterspout" },
  'hurricane': { role: 'hazard', category: 'incident', label: "Hurricane / tropical storm" },
  'ice-storm': { role: 'hazard', category: 'incident', label: "Ice storm / freezing rain" },
  'blizzard': { role: 'hazard', category: 'incident', label: "Blizzard / heavy snow" },
  'extreme-heat': { role: 'hazard', category: 'incident', label: "Extreme heat" },
  'severe-storm': { role: 'hazard', category: 'incident', label: "Severe storm / thunderstorm" },
  'hazmat': { role: 'hazard', category: 'incident', label: "Hazardous materials" },
  'smoke': { role: 'hazard', category: 'incident', label: "Smoke plume" },
  'hot-spot': { role: 'hazard', category: 'incident', label: "Fire hot spot" },
  'air-quality': { role: 'hazard', category: 'incident', label: "Air quality / smog" },
  'avalanche': { role: 'hazard', category: 'incident', label: "Avalanche" },
  'biohazard': { role: 'hazard', category: 'incident', label: "Biological hazard" },
  'chemical': { role: 'hazard', category: 'incident', label: "Chemical hazard" },
  'landslide': { role: 'hazard', category: 'incident', label: "Landslide" },
  'nuclear-facility': { role: 'hazard', category: 'incident', label: "Nuclear facility / CBRN" },
  'radiological': { role: 'hazard', category: 'incident', label: "Radiological hazard" },
  'rail-incident': { role: 'hazard', category: 'incident', label: "Rail incident" },
  'comms-tower': { role: 'asset', category: 'infrastructure', label: "Communications tower" },
  'hospital': { role: 'asset', category: 'operations', label: "Hospital / medical facility" },
  'emergency-ops-centre': { role: 'asset', category: 'operations', label: "Emergency operations centre" },
  'fire-station': { role: 'asset', category: 'operations', label: "Fire station" },
  'staging-area': { role: 'asset', category: 'operations', label: "Staging area" },
  'assembly-area': { role: 'asset', category: 'operations', label: "Assembly area" },
  'ambulance': { role: 'asset', category: 'operations', label: "Ambulance / EMS" },
  'medevac': { role: 'asset', category: 'operations', label: "Medical evacuation" },
  'decontamination': { role: 'asset', category: 'operations', label: "Decontamination" },
  'evacuation': { role: 'asset', category: 'operations', label: "Evacuation" },
  'emergency-shelter': { role: 'asset', category: 'operations', label: "Emergency shelter" },
  'police': { role: 'asset', category: 'operations', label: "Police" },
  'water-distribution': { role: 'asset', category: 'operations', label: "Water distribution" },
  'power': { role: 'asset', category: 'infrastructure', label: "Power / electrical" },
  'road-closure': { role: 'asset', category: 'infrastructure', label: "Road closure" },
  'frame-incident': { role: 'frame', category: 'incident', label: "Incident frame (diamond)" },
  'frame-infrastructure': { role: 'frame', category: 'infrastructure', label: "Infrastructure frame (rectangle)" },
  'frame-operations': { role: 'frame', category: 'operations', label: "Operations frame (circle)" },
};

/** Declaration-ordered list of every glyph key. */
export const CAHS_GLYPH_KEYS = Object.keys(CAHS_GLYPHS) as CahsGlyphKey[];
